package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.*
import com.arenaproplay.admin.pages.system.*
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.utils.periodFromUrl
import com.arenaproplay.admin.ui.utils.setUrlParams
import com.arenaproplay.admin.ui.utils.urlParam
import org.jetbrains.compose.web.css.*

// ── Tab enum ──────────────────────────────────────────────────────────────────

private enum class SystemTab(val code: String, val label: String) {
    ADMINS("admins", "Админы"),
    AUDIT("audit", "Аудит"),
    API("api", "API");

    companion object {
        fun fromCode(code: String?): SystemTab =
            entries.find { it.code == code } ?: ADMINS
    }
}

// ── Page ──────────────────────────────────────────────────────────────────────

/**
 * Система — consolidates Admins, Audit Log, and API Stats as tabs.
 *
 * Tab implementations live in pages/system/{AdminsTab,AuditTab,ApiTab}.kt.
 *
 * URL state:
 *   ?tab=admins|audit|api  (default: admins)
 *   Admins: ?page=N ?q=text
 *   Audit:  ?offset=N ?q=text
 *   API:    ?period=day|week|month|all
 */
@Page("/system")
@Composable
fun SystemPage() {
    val container = LocalAppContainer.current
    val repo = container.adminRepository ?: return

    var activeTab by remember { mutableStateOf(SystemTab.fromCode(urlParam("tab"))) }

    // ── Admins state ──────────────────────────────────────────────────────────
    var adminsPage by remember { mutableStateOf(urlParam("page")?.toIntOrNull()?.coerceAtLeast(0) ?: 0) }
    var adminsQuery by remember { mutableStateOf(urlParam("q") ?: "") }
    var adminsData by remember { mutableStateOf<PageResult<UserRow>?>(null) }
    val adminsLoadState = rememberLoadState()

    // ── Audit state ────────────────────────────────────────────────────────────
    var auditOffset by remember { mutableStateOf(urlParam("offset")?.toLongOrNull()?.coerceAtLeast(0L) ?: 0L) }
    var auditQuery by remember { mutableStateOf(urlParam("q") ?: "") }
    var auditData by remember { mutableStateOf<List<AuditEntry>?>(null) }
    val auditLoadState = rememberLoadState()

    // ── API state ──────────────────────────────────────────────────────────────
    var apiPeriod by remember { mutableStateOf(periodFromUrl() ?: Period.MONTH) }
    var apiTotals by remember { mutableStateOf<List<ApiRequestStats>?>(null) }
    var apiByPlatform by remember { mutableStateOf<List<ApiPlatformBreakdown>>(emptyList()) }
    var apiErrors by remember { mutableStateOf<List<ApiErrorRow>?>(null) }
    val apiLoadState = rememberLoadState()

    // ── URL sync — live (keys include per-tab state) ──────────────────────────
    LaunchedEffect(activeTab, adminsPage, adminsQuery, auditOffset, auditQuery, apiPeriod) {
        when (activeTab) {
            SystemTab.ADMINS -> setUrlParams(
                "tab" to null,
                "page" to adminsPage.takeIf { it > 0 }?.toString(),
                "q" to adminsQuery.ifBlank { null },
                "offset" to null,
                "period" to null,
            )
            SystemTab.AUDIT -> setUrlParams(
                "tab" to SystemTab.AUDIT.code,
                "offset" to auditOffset.takeIf { it > 0L }?.toString(),
                "q" to auditQuery.ifBlank { null },
                "page" to null,
                "period" to null,
            )
            SystemTab.API -> setUrlParams(
                "tab" to SystemTab.API.code,
                "period" to apiPeriod.code.takeIf { apiPeriod != Period.MONTH },
                "page" to null,
                "q" to null,
                "offset" to null,
            )
        }
    }

    // ── Admins data loading ────────────────────────────────────────────────────
    LaunchedEffect(adminsPage, adminsQuery, adminsLoadState.reloadToken, activeTab) {
        if (activeTab != SystemTab.ADMINS) return@LaunchedEffect
        adminsData = null
        adminsLoadState.load {
            adminsData = repo.getUsers(adminsPage, SYSTEM_ADMINS_PAGE_SIZE, adminsQuery)
        }
    }

    val adminsFiltered = adminsData?.items?.filter {
        it.role.equals("ADMIN", ignoreCase = true) ||
            it.role.uppercase().contains("SUPER")
    }.orEmpty()

    // ── Audit data loading ─────────────────────────────────────────────────────
    LaunchedEffect(auditOffset, auditLoadState.reloadToken, activeTab) {
        if (activeTab != SystemTab.AUDIT) return@LaunchedEffect
        auditData = null
        auditLoadState.load {
            auditData = repo.getAuditEntries(SYSTEM_AUDIT_PAGE_SIZE, auditOffset)
        }
    }

    val auditFiltered = auditData?.filter {
        auditQuery.isBlank() ||
            it.action.contains(auditQuery, ignoreCase = true) ||
            it.adminId.contains(auditQuery, ignoreCase = true) ||
            it.targetKind?.contains(auditQuery, ignoreCase = true) == true ||
            it.targetId?.contains(auditQuery, ignoreCase = true) == true
    }.orEmpty()

    // ── API data loading ────────────────────────────────────────────────────────
    LaunchedEffect(apiPeriod, apiLoadState.reloadToken, activeTab) {
        if (activeTab != SystemTab.API) return@LaunchedEffect
        apiTotals = null
        apiErrors = null
        apiLoadState.load {
            apiTotals = repo.getApiRequests(apiPeriod)
            apiByPlatform = repo.getApiRequestsByPlatform(apiPeriod)
            apiErrors = repo.getApiErrors(apiPeriod)
        }
    }

    val apiRows = remember(apiTotals, apiByPlatform, apiErrors) {
        sysBuildRows(apiTotals.orEmpty(), apiByPlatform, apiErrors.orEmpty())
    }

    val apiActivePlatforms = remember(apiByPlatform) {
        val present = apiByPlatform.flatMap { it.byPlatform.keys }.toSet()
        SYS_KNOWN_PLATFORMS + present
            .filter { it !in SYS_KNOWN_PLATFORMS && it !in SYS_HIDDEN_PLATFORMS }
            .sorted()
    }

    val apiTotalRequests = remember(apiRows) { apiRows.sumOf { it.total } }
    val apiTotalErrors = remember(apiRows) { apiRows.sumOf { it.errors } }
    val apiAvgLatencyMs = remember(apiRows) {
        if (apiRows.isEmpty()) 0.0
        else apiRows.sumOf { it.avgLatencyMs * it.total } / apiRows.sumOf { it.total }.coerceAtLeast(1)
    }
    val apiMaxLatencyMs = remember(apiRows) { apiRows.maxOfOrNull { it.maxLatencyMs } ?: 0 }

    AdminLayout(
        title = "Система",
        subtitle = when (activeTab) {
            SystemTab.ADMINS -> "Список администраторов платформы (только просмотр)"
            SystemTab.AUDIT -> "Кто, что и когда изменил"
            SystemTab.API -> "Каждый эндпоинт, каждая платформа за выбранный период"
        },
        actions = {
            when (activeTab) {
                SystemTab.ADMINS -> {
                    SearchInput(
                        value = adminsQuery,
                        onValueChange = { adminsQuery = it; adminsPage = 0 },
                        placeholderText = "Поиск по имени или email…",
                    )
                }
                SystemTab.AUDIT -> {
                    SearchInput(
                        value = auditQuery,
                        onValueChange = { auditQuery = it },
                        placeholderText = "Фильтр по действию, администратору или объекту…",
                    )
                }
                SystemTab.API -> {
                    PeriodSelector(
                        apiPeriod,
                        options = listOf(Period.DAY, Period.WEEK, Period.MONTH, Period.ALL),
                    ) { apiPeriod = it }
                }
            }
        },
    ) {
        // ── Page title + tab switcher ─────────────────────────────────────────
        Row(
            Modifier.fillMaxWidth()
                .styleModifier {
                    property("align-items", "flex-end")
                    property("justify-content", "space-between")
                    property("gap", "20px")
                }
        ) {
            SpanText(
                "Система",
                Modifier.styleModifier {
                    property("font-family", "'Unbounded', sans-serif")
                    property("font-size", "24px")
                    property("font-weight", "700")
                    property("letter-spacing", "-0.02em")
                    property("color", "#F0F4FC")
                }
            )

            SystemTabSwitcher(active = activeTab, onSelect = { activeTab = it })
        }

        VSpace(20)

        // ── Tab content — fade-in on switch ──────────────────────────────────
        key(activeTab) {
            Box(Modifier.fillMaxWidth().classNames("na-fade-in")) {
                Column(Modifier.fillMaxWidth()) {
                    when (activeTab) {
                        SystemTab.ADMINS -> SystemAdminsTab(
                            loadState = adminsLoadState,
                            data = adminsData,
                            admins = adminsFiltered,
                            page = adminsPage,
                            onPagePrev = { if (adminsPage > 0) adminsPage-- },
                            onPageNext = {
                                val total = adminsData?.total ?: 0
                                val size = adminsData?.pageSize ?: SYSTEM_ADMINS_PAGE_SIZE
                                if ((adminsPage + 1) * size < total) adminsPage++
                            },
                        )
                        SystemTab.AUDIT -> SystemAuditTab(
                            loadState = auditLoadState,
                            data = auditData,
                            filtered = auditFiltered,
                            offset = auditOffset,
                            onPrev = { auditOffset = (auditOffset - SYSTEM_AUDIT_PAGE_SIZE).coerceAtLeast(0) },
                            onNext = {
                                if ((auditData?.size ?: 0) == SYSTEM_AUDIT_PAGE_SIZE)
                                    auditOffset += SYSTEM_AUDIT_PAGE_SIZE
                            },
                        )
                        SystemTab.API -> SystemApiTab(
                            loadState = apiLoadState,
                            totals = apiTotals,
                            rows = apiRows,
                            activePlatforms = apiActivePlatforms,
                            errors = apiErrors,
                            totalRequests = apiTotalRequests,
                            totalErrors = apiTotalErrors,
                            avgLatencyMs = apiAvgLatencyMs,
                            maxLatencyMs = apiMaxLatencyMs,
                        )
                    }
                }
            }
        }

        VSpace(40)
    }
}

// ── Tab switcher ──────────────────────────────────────────────────────────────

@Composable
private fun SystemTabSwitcher(active: SystemTab, onSelect: (SystemTab) -> Unit) {
    Row(
        Modifier.styleModifier {
            property("display", "flex")
            property("gap", "2px")
            property("background", "rgba(255,255,255,.05)")
            property("border", "1px solid rgba(255,255,255,.08)")
            property("border-radius", "12px")
            property("padding", "3px")
        }
    ) {
        SystemTab.entries.forEach { tab ->
            SystemTabChip(
                label = tab.label,
                active = active == tab,
                onClick = { onSelect(tab) }
            )
        }
    }
}

@Composable
private fun SystemTabChip(label: String, active: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .styleModifier {
                property("font-size", "13px")
                property("padding", "6px 16px")
                property("border-radius", "9px")
                property("cursor", "pointer")
                property("user-select", "none")
                property("transition", "all 160ms ease")
                property("white-space", "nowrap")
                property("font-family", "'Inter', sans-serif")
                property("font-weight", if (active) "600" else "400")
                if (active) {
                    property("background", "rgba(0,97,255,.30)")
                    property("color", "#8FB8FF")
                } else {
                    property("color", "rgba(240,244,252,.50)")
                }
            }
            .classNames("na-press")
            .onClick { onClick() }
    ) {
        SpanText(label, Modifier)
    }
}
